import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Paginacion from '../../components/common/Paginacion';
import usePaginacion from '../../hooks/usePaginacion';
import usuarioService from '../../services/usuarioService';
import { useToast } from '../../context/ToastContext';
import { formatearFecha } from '../../utils/helpers';
import { validarEmail, validarTexto, validarPassword } from '../../utils/validaciones';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaSearch } from 'react-icons/fa';

const FORM_INICIAL = {
  nombre: '', apellido: '', email: '',
  password: '', rol: 'usuario',
};

export default function UsuariosPage() {
  const { mostrar }                 = useToast();
  const [usuarios,     setUsuarios] = useState([]);
  const [cargando,     setCargando] = useState(true);
  const [guardando,    setGuardando] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando,     setEditando] = useState(null);
  const [busqueda,     setBusqueda] = useState('');
  const [form,         setForm]     = useState(FORM_INICIAL);
  const [errores,      setErrores]  = useState({});

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const data = await usuarioService.listar();
      setUsuarios(data);
    } finally {
      setCargando(false);
    }
  };

  // Filtro por búsqueda
  const usuariosFiltrados = usuarios.filter((u) => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (
      u.nombre?.toLowerCase().includes(q) ||
      u.apellido?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.rol?.toLowerCase().includes(q)
    );
  });

  const { datosPaginados, pagina, totalPaginas, cambiarPagina, setPagina } =
    usePaginacion(usuariosFiltrados);

  const abrirModal = (usuario = null) => {
    setEditando(usuario);
    setErrores({});
    setForm(usuario ? {
      nombre:   usuario.nombre,
      apellido: usuario.apellido,
      email:    usuario.email,
      password: '',
      rol:      usuario.rol,
    } : FORM_INICIAL);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
    setForm(FORM_INICIAL);
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errores[name]) setErrores((prev) => ({ ...prev, [name]: null }));
  };

  // Validación del formulario
  const validar = () => {
    const e = {};
    const nombre   = validarTexto(form.nombre, 'Nombre');
    const apellido = validarTexto(form.apellido, 'Apellido');
    const email    = validarEmail(form.email);
    if (nombre)   e.nombre   = nombre;
    if (apellido) e.apellido = apellido;
    if (email)    e.email    = email;
    if (!editando) {
      const pwd = validarPassword(form.password);
      if (pwd) e.password = pwd;
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroresForm = validar();
    if (Object.keys(erroresForm).length > 0) {
      setErrores(erroresForm);
      return;
    }
    setGuardando(true);
    try {
      const datos = { ...form };
      if (editando && !datos.password) delete datos.password;
      if (editando) {
        await usuarioService.actualizar(editando.id, datos);
        mostrar('Usuario actualizado correctamente.');
      } else {
        await usuarioService.crear(datos);
        mostrar('Usuario creado correctamente.');
      }
      cerrarModal();
      cargarDatos();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al guardar el usuario.';
      mostrar(msg, 'danger');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar definitivamente este usuario? Esta acción no se puede deshacer.')) return;
    try {
      await usuarioService.eliminar(id);
      mostrar('Usuario eliminado correctamente.');
      cargarDatos();
    } catch (err) {
      mostrar(err.response?.data?.message || 'Error al eliminar el usuario.', 'danger');
    }
  };

  const getBadgeRol = (rol) => {
    const colores = {
      administrador: 'bg-danger',
      contador:      'bg-warning text-dark',
      usuario:       'bg-primary',
    };
    return colores[rol] || 'bg-secondary';
  };

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h4>Usuarios</h4>
          <p>Gestión de usuarios del sistema</p>
        </div>
        <button className="btn btn-danger" onClick={() => abrirModal()}>
          <FaPlus className="me-2" /> Nuevo Usuario
        </button>
      </div>

      {/* Búsqueda */}
      <div className="card card-custom mb-3">
        <div className="card-body py-2">
          <div className="input-group input-group-sm" style={{ maxWidth: '400px' }}>
            <span className="input-group-text"><FaSearch /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, email o rol..."
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
            />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card card-custom">
        <div className="card-body">
          {cargando ? (
            <div className="text-center py-4">
              <div className="spinner-custom mx-auto" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-custom">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th>Último Acceso</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosPaginados.length > 0 ? (
                      datosPaginados.map((u) => (
                        <tr key={u.id}>
                          <td className="fw-bold">{u.nombre} {u.apellido}</td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`badge ${getBadgeRol(u.rol)}`}>
                              {u.rol}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${u.activo ? 'badge-activo' : 'badge-anulado'}`}>
                              {u.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td>{formatearFecha(u.ultimo_acceso) || '—'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => abrirModal(u)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleEliminar(u.id)}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          <FaUsers size={32} className="mb-2 d-block mx-auto" />
                          {busqueda ? 'Sin resultados para la búsqueda' : 'Sin usuarios registrados'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Paginacion
                paginaActual={pagina}
                totalPaginas={totalPaginas}
                onCambiar={cambiarPagina}
              />
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-header-custom">
              <h5>{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h5>
              <button className="btn-close-custom" onClick={cerrarModal}>×</button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Nombre *</label>
                    <input
                      name="nombre"
                      className={`form-control ${errores.nombre ? 'is-invalid' : ''}`}
                      value={form.nombre}
                      onChange={handleChange}
                      maxLength={20}
                    />
                    {errores.nombre && (
                      <div className="invalid-feedback">{errores.nombre}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Apellido *</label>
                    <input
                      name="apellido"
                      className={`form-control ${errores.apellido ? 'is-invalid' : ''}`}
                      value={form.apellido}
                      onChange={handleChange}
                      maxLength={20}
                    />
                    {errores.apellido && (
                      <div className="invalid-feedback">{errores.apellido}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Email *</label>
                    <input
                      name="email"
                      type="email"
                      className={`form-control ${errores.email ? 'is-invalid' : ''}`}
                      value={form.email}
                      onChange={handleChange}
                      maxLength={30}
                    />
                    {errores.email && (
                      <div className="invalid-feedback">{errores.email}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      {editando ? 'Nueva Contraseña (opcional)' : 'Contraseña *'}
                    </label>
                    <input
                      name="password"
                      type="password"
                      className={`form-control ${errores.password ? 'is-invalid' : ''}`}
                      value={form.password}
                      onChange={handleChange}
                      minLength={6}
                    />
                    {errores.password && (
                      <div className="invalid-feedback">{errores.password}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Rol *</label>
                    <select
                      name="rol"
                      className="form-select"
                      value={form.rol}
                      onChange={handleChange}
                    >
                      <option value="usuario">Usuario</option>
                      <option value="contador">Contador</option>
                      <option value="administrador">Administrador</option>
                    </select>
                  </div>

                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cerrarModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={guardando}
                  >
                    {guardando ? 'Guardando...' : editando ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}